import type { CreateBrandNoteDTO } from "@nlc/shared-types";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { brandNotesKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseCreateBrandNoteParams {
  brandId: number;
}

export function useCreateBrandNote({
  brandId,
}: UseCreateBrandNoteParams): UseMutationResult<
  unknown,
  unknown,
  CreateBrandNoteDTO
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBrandNoteDTO) =>
      apiClient.url(`/brands/${brandId}/notes`).post(data).res(),

    onError: (error: unknown) => {
      const errorMessage =
        (error as { message?: string })?.message || "Failed to create note";

      toast.add({
        description: errorMessage,
        timeout: 3000,
        title: "Error",
        type: "error",
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: brandNotesKeys.byBrandId(brandId),
      });

      toast.add({
        description: "Note added to the knowledge base",
        timeout: 2000,
        title: "Note created",
        type: "success",
      });
    },
  });
}
